import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { useLanguage } from '@/hooks/useLanguage';
import { useToast } from '@/components/ui/use-toast';
import { Link } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import { WELCOME_TRANSLATIONS } from '@/components/LanguageBubble';

const ProfileSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { settings, updateSetting, isLoading } = useAccessibility();
  const { language, setLanguage, isLoading: languageLoading } = useLanguage(user?.id);
  const { toast } = useToast();

  // Handle language change
  const handleLanguageChange = async (code: string) => {
    await setLanguage(code);
    toast({
      title: t('settings.languageChanged'),
      description: t('settings.languageChangedDesc'),
      className: 'bg-maslow-navy text-maslow-cream',
    });
  };

  // Handle accessibility setting change with toast
  const handleSettingChange = async (key: 'reduce_animations' | 'high_contrast' | 'larger_text', value: boolean) => {
    await updateSetting(key, value);
    toast({
      title: t('settings.saved'),
      description: t('settings.savedDesc'),
      className: 'bg-maslow-navy text-maslow-cream',
    });
  };

  // Handle sign out
  const handleSignOut = async () => {
    if (window.confirm(t('settings.signOutConfirm'))) {
      await signOut();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-maslow-cream">
        <Loader2 className="animate-spin w-6 h-6 text-[color-mix(in_srgb,var(--navy)_40%,transparent)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-maslow-cream pb-24" style={{ fontFamily: 'var(--sans)' }}>

      {/* Header — compact, matches /profile and the public pages */}
      <section className="relative pt-10 pb-4 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-maslow-navy mb-3">
            {t('settings.title')}
          </h1>
          <p className="text-base md:text-lg text-[color-mix(in_srgb,var(--navy)_75%,transparent)] font-light">
            {t('settings.subtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-6 pt-4">

        {/* One contained panel — same surface as the profile tabs */}
        <div className="bg-[color-mix(in_srgb,#ffffff_50%,var(--cream))] border border-[rgba(212,175,106,0.3)] rounded-sm shadow-[0_24px_60px_-40px_rgba(28,43,58,0.45)] p-5 md:p-8">

          {/* Preferences */}
          <SectionLabel>{t('settings.preferences')}</SectionLabel>
          <div className="mt-3 mb-8">
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 py-3">
              <span className="min-w-0">
                <span className="block text-[17px] text-maslow-navy" style={{ fontFamily: 'var(--serif)' }}>{t('settings.language')}</span>
                <span className="block text-sm text-[color-mix(in_srgb,var(--navy)_60%,transparent)] mt-0.5 leading-snug">{t('settings.languageDesc')}</span>
              </span>
              <select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                disabled={languageLoading}
                className="bg-transparent text-base text-maslow-navy border-0 border-b border-[color-mix(in_srgb,var(--navy)_25%,transparent)] focus:border-maslow-gold rounded-none px-0 py-1.5 outline-none appearance-none cursor-pointer transition-colors"
              >
                {WELCOME_TRANSLATIONS.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.language}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Accessibility */}
          <SectionLabel>{t('settings.accessibility')}</SectionLabel>
          <div className="mt-3 mb-8">
            <ToggleRow
              label={t('settings.reduceAnimations')}
              description={t('settings.reduceAnimationsDesc')}
              checked={settings.reduce_animations}
              onChange={() => handleSettingChange('reduce_animations', !settings.reduce_animations)}
            />
            <ToggleRow
              label={t('settings.highContrast')}
              description={t('settings.highContrastDesc')}
              checked={settings.high_contrast}
              onChange={() => handleSettingChange('high_contrast', !settings.high_contrast)}
            />
            <ToggleRow
              label={t('settings.largerText')}
              description={t('settings.largerTextDesc')}
              checked={settings.larger_text}
              onChange={() => handleSettingChange('larger_text', !settings.larger_text)}
            />
          </div>

          {/* Support */}
          <SectionLabel>{t('settings.support')}</SectionLabel>
          <div className="mt-3">
            <LinkRow to="/faq" label={t('settings.faq', 'FAQ')} />
            <LinkRow href="mailto:hello@maslow.nyc?subject=Support Request" label={t('settings.contactSupport', 'Contact Support')} />
            <LinkRow to="/terms-of-service" label={t('settings.terms', 'Terms & Conditions')} />
            <LinkRow to="/privacy-policy" label={t('settings.privacy', 'Privacy Policy')} />
          </div>
        </div>

        {/* Quiet footer row — back link left, sign out right */}
        <div className="pt-8 flex items-baseline justify-between">
          <Link to="/profile" className="text-sm text-[color-mix(in_srgb,var(--navy)_55%,transparent)] hover:text-maslow-gold transition-colors">
            &larr; {t('navigation.profile')}
          </Link>
          <button
            onClick={handleSignOut}
            className="text-sm text-[color-mix(in_srgb,#b91c1c_80%,transparent)] hover:text-[#b91c1c] transition-colors"
          >
            {t('settings.signOut')}
          </button>
        </div>

        <p className="mt-6 text-center text-[color-mix(in_srgb,var(--navy)_40%,transparent)] text-xs tracking-wide">
          Maslow v2.1.0
        </p>
      </div>
    </div>
  );
};

// ── Sub-components (same language as /profile) ─────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-[13px] font-bold uppercase tracking-[0.24em] text-[color-mix(in_srgb,var(--gold)_75%,var(--navy))]">{children}</h2>
);

const ToggleRow: React.FC<{ label: string; description?: string; checked: boolean; onChange: () => void }> = ({ label, description, checked, onChange }) => (
  <button
    onClick={onChange}
    role="switch"
    aria-checked={checked}
    className="group w-full flex items-start justify-between gap-6 py-3 border-b border-[color-mix(in_srgb,var(--navy)_7%,transparent)] last:border-b-0 text-left transition-colors"
  >
    <span className="min-w-0">
      <span className="block text-[17px] text-maslow-navy" style={{ fontFamily: 'var(--serif)' }}>{label}</span>
      {description && (
        <span className="block text-sm text-[color-mix(in_srgb,var(--navy)_60%,transparent)] mt-0.5 leading-snug">{description}</span>
      )}
    </span>
    <span className={`mt-1 w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
      checked
        ? 'border-maslow-gold bg-maslow-gold'
        : 'border-[color-mix(in_srgb,var(--navy)_25%,transparent)] group-hover:border-[color-mix(in_srgb,var(--gold)_60%,transparent)]'
    }`}>
      {checked && <Check className="w-2.5 h-2.5 text-maslow-navy" strokeWidth={3} />}
    </span>
  </button>
);

const LinkRow: React.FC<{ label: string; to?: string; href?: string }> = ({ label, to, href }) => {
  const className = "group w-full flex items-baseline justify-between gap-6 py-3 border-b border-[color-mix(in_srgb,var(--navy)_7%,transparent)] last:border-b-0 transition-colors";
  const inner = (
    <>
      <span className="text-[17px] text-maslow-navy group-hover:text-maslow-gold transition-colors" style={{ fontFamily: 'var(--serif)' }}>{label}</span>
      <span className="text-sm text-[color-mix(in_srgb,var(--navy)_40%,transparent)] group-hover:text-maslow-gold transition-colors">&rarr;</span>
    </>
  );
  return to
    ? <Link to={to} className={className}>{inner}</Link>
    : <a href={href} className={className}>{inner}</a>;
};

export default ProfileSettingsPage;
